/**
 * Inventory shared helpers — quantity parsing, reagent select options, and the visual mapping of the
 * reorder bucket (OK|BAS|RUPTURE) and the near-expiry tier (ok|alerte|critique) to a Badge tone + glyph.
 *
 * Every status chip combines COLOR + GLYPH + TEXT (daltonisme + médico-légal); we never rely on color
 * alone (the Badge primitive enforces the glyph for clinical tones, and we pass an explicit glyph for
 * the operational success/warn/danger tones used here).
 */
import type { BadgeTone } from "@/ui";
import type { SelectOption } from "@/ui";
import type { MouvementType, Reactif, ReorderBucket, RiskTier } from "./types";
import { idStr } from "./types";

/** Parse an editable quantity string ("12" or "12.5") into a non-negative number, or null if invalid. */
export function parseQty(input: string): number | null {
  const trimmed = input.trim().replace(",", ".");
  if (trimmed === "") return null;
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const v = Number.parseFloat(trimmed);
  return Number.isFinite(v) && v >= 0 ? v : null;
}

/** Format a quantity for display (mono island handles LTR). "—" when absent. */
export function fmtQty(q: number | null | undefined): string {
  if (q == null || !Number.isFinite(q)) return "—";
  return Number.isInteger(q) ? String(q) : String(q);
}

/** Reagent <Select> options: "CODE — Libellé", sorted by code (the catalog is already code-sorted). */
export function reactifOptions(reactifs: ReadonlyArray<Reactif>): SelectOption[] {
  return reactifs.map((r) => ({ value: idStr(r.id), label: `${r.code} — ${r.libelle}` }));
}

/** Find a reagent by its stringified id. */
export function findReactif(
  reactifs: ReadonlyArray<Reactif>,
  id: string | null | undefined,
): Reactif | undefined {
  if (!id) return undefined;
  return reactifs.find((r) => idStr(r.id) === id);
}

/** Visual descriptor for a reorder bucket — tone + glyph (shape carries meaning, not color alone). */
export interface StatusVisual {
  tone: BadgeTone;
  glyph: string;
  /** i18n key for the label. */
  labelKey: string;
}

const BUCKET_VISUAL: Record<ReorderBucket, StatusVisual> = {
  OK: { tone: "success", glyph: "●", labelKey: "inventory.reorder.bucket.OK" },
  BAS: { tone: "warn", glyph: "▲", labelKey: "inventory.reorder.bucket.BAS" },
  RUPTURE: { tone: "danger", glyph: "■", labelKey: "inventory.reorder.bucket.RUPTURE" },
};

export function bucketVisual(bucket: ReorderBucket): StatusVisual {
  return BUCKET_VISUAL[bucket] ?? BUCKET_VISUAL.OK;
}

const TIER_VISUAL: Record<RiskTier, StatusVisual> = {
  ok: { tone: "success", glyph: "●", labelKey: "inventory.lots.tier.ok" },
  alerte: { tone: "warn", glyph: "▲", labelKey: "inventory.lots.tier.alerte" },
  critique: { tone: "danger", glyph: "◆", labelKey: "inventory.lots.tier.critique" },
};

export function tierVisual(tier: RiskTier): StatusVisual {
  return TIER_VISUAL[tier] ?? TIER_VISUAL.ok;
}

/** The token CSS var used to color a reorder bucket in the chart (no raw hex, L-2). */
export const BUCKET_TOKEN: Record<ReorderBucket, string> = {
  OK: "var(--nv-success)",
  BAS: "var(--nv-warn)",
  RUPTURE: "var(--nv-danger)",
};

/** Visual descriptor for a ledger movement type (NOVA-058) — tone + glyph + i18n label key. */
const MOUVEMENT_VISUAL: Record<string, StatusVisual> = {
  entree: { tone: "success", glyph: "▲", labelKey: "inventory.movements.type.entree" },
  sortie: { tone: "info", glyph: "▼", labelKey: "inventory.movements.type.sortie" },
  ajustement: { tone: "warn", glyph: "±", labelKey: "inventory.movements.type.ajustement" },
  peremption: { tone: "danger", glyph: "◆", labelKey: "inventory.movements.type.peremption" },
};

export function mouvementVisual(type: MouvementType): StatusVisual {
  return MOUVEMENT_VISUAL[type] ?? { tone: "neutral", glyph: "●", labelKey: "inventory.movements.type.entree" };
}
